import React, { memo, useEffect, useMemo, useState } from 'react';
import { Modal } from 'app/shared/modal/modal';
import { Searchbar } from 'app/components/Searchbar';
import { Button, Space, Tabs, Tree } from 'antd';
import { ModalContent } from 'app/modules/setting/components/lending/modalContent';
import { filter, find, map } from 'underscore';
import { connect } from 'react-redux';
import { swapArrayLoc } from '../footer';
import { fetchSearchShopList } from 'app/modules/shop/actions/getListSearch';
import classNames from 'classnames';
import { CustomModal } from 'app/shared/customModal/customModal';
import { useDesktop } from 'app/hooks/useDesktop';
import s from './index.module.scss';
export const LendingProductsModalContent = connect((state) => ({
    productsSearch: state.shopState.productsSearch,
    point: state.shopState.order.point,
}))(
    memo(({ onSubmit, initialItems = [], dispatch, productsSearch, point }) => {
        const [items, setItems] = useState(initialItems);
        const { response } = productsSearch;
        function onChange(checked, item) {
            setItems((prevState) =>
                checked
                    ? [...prevState, item]
                    : filter(prevState, (i) => i.id !== item.id)
            );
        }

        function onChangeOrder(index, up) {
            setItems((prev) =>
                swapArrayLoc(prev, index, up ? index - 1 : index + 1)
            );
        }

        useEffect(() => {
            if (point && productsSearch?.isNotReady) {
                dispatch(
                    fetchSearchShopList({
                        text: '',
                        point: point.id,
                        row: 25,
                    })
                );
            }
        }, []);

        return (
            <ModalContentContainer
                header={
                    <Searchbar showOptions={false} style={{ width: '100%' }} />
                }
                modifier={'products'}
                tabsClassname={classNames({
                    'tabs-loading': productsSearch?.isFetching,
                })}
                onSubmit={() => onSubmit(items)}
                added={
                    <ModalContent
                        product
                        items={items}
                        onChange={onChange}
                        onOrder={onChangeOrder}
                    />
                }
            >
                <ModalContent
                    isLoading={productsSearch?.isFetching}
                    className={'child-type'}
                    checked={({ id }) => find(items, (item) => item.id === id)}
                    items={response?.data || []}
                    onChange={onChange}
                />
            </ModalContentContainer>
        );
    })
);

function formatNode(nodes) {
    return map(nodes, ({ id, name, children }) => {
        return {
            key: id,
            title: name,
            children: children ? formatNode(children) : [],
        };
    });
}

export function formatNodesBack(nodes) {
    return map(nodes, ({ key, title, children }) => {
        return {
            id: key,
            name: title,
            children: children ? formatNodesBack(children) : [],
        };
    });
}
function formatNodeBack({ key, title, children }) {
    return {
        id: key,
        name: title,
        children,
    };
}

export function flatterTree(tree) {
    const nodes = [];
    function call(node) {
        nodes.push(node.id);
        if (node.children?.length) {
            for (let i = 0; i < node.children?.length; i++) {
                call(node.children[i]);
            }
        }
    }
    if (tree?.length) {
        for (let i = 0; i < tree.length; i++) {
            call(tree[i]);
        }
    }
    return nodes;
}

function setNode(id, tree) {
    function set(node) {
        if (node.id === id) {
            node.checked = true;
        }
        if (node.children.length) {
            for (let i = 0; i < node.children.length; i++) {
                set(node.children[i]);
            }
        }
    }
    for (let i = 0; i < tree.length; i++) {
        set(tree[i]);
    }
}

function findHalfChecked(tree) {
    const halfChecked = [];
    const checked = [];
    function isChecked(node, path = []) {
        if (!node.checked)
            path.forEach((node) => {
                if (node.checked) {
                    node.halfChecked = true;
                    halfChecked.push(node.id);
                }
            });
        if (node.checked) checked.push(node.id);
        if (node.children.length) {
            for (let i = 0; i < node.children.length; i++) {
                isChecked(node.children[i], [...path, node]);
            }
        }
    }
    for (let i = 0; i < tree.length; i++) {
        isChecked(tree[i]);
    }
    const setHalfChecked = [...new Set(halfChecked)];
    const setChecked = [...new Set(checked)];
    return {
        halfChecked: setHalfChecked || [],
        checked:
            setChecked?.filter((node) => !setHalfChecked.includes(node)) || [],
    };
}

function compareTrees(initialTree, currentTree) {
    const copy = JSON.parse(JSON.stringify(initialTree));
    const currentTreeNodes = flatterTree(currentTree);
    for (let i = 0; i < currentTreeNodes.length; i++) {
        const node = currentTreeNodes[i];
        setNode(node, copy);
    }
    return findHalfChecked(copy);
}

export const LendingTypesModalContent = memo(
    ({
        onSubmit,
        checkStrictly,
        tree = [],
        initialItems = [],
        showAdded = true,
    }) => {
        const [halfCheckedKeys, setHalfCheckedKeys] = useState([]);
        const [items, setItems] = useState(checkStrictly ? initialItems : []);

        useEffect(() => {
            if (!checkStrictly) {
                const { halfChecked, checked } = compareTrees(
                    tree,
                    initialItems
                );
                setItems(checked);
                setHalfCheckedKeys(halfChecked);
            }
        }, [tree, initialItems]);

        const [expanded, setExpanded] = useState([]);

        const treeData = useMemo(() => {
            return formatNode(tree);
        }, [tree]);

        function onChange(checked, item) {
            setItems((prevState) =>
                checked
                    ? [...prevState, item]
                    : filter(prevState, (i) => i.id !== item.id)
            );
        }
        function onCheck(checkedKeys, info) {
            setItems((prevState) => {
                return checkStrictly
                    ? info.checked
                        ? [...prevState, formatNodeBack(info.node)]
                        : filter(prevState, (i) => i.id !== info.node.key)
                    : checkedKeys;
            });
            if (!checkStrictly) setHalfCheckedKeys(info.halfCheckedKeys);
        }

        function onChangeOrder(index, up) {
            setItems((prev) =>
                swapArrayLoc(prev, index, up ? index - 1 : index + 1)
            );
        }

        function onModalSubmit() {
            onSubmit(checkStrictly ? items : [...halfCheckedKeys, ...items]);
        }

        function onExpand(expandedKeys) {
            setExpanded(expandedKeys);
        }

        function onSelect(selectedKeys, event) {
            setExpanded((prevState) =>
                event.node.expanded
                    ? filter(prevState, (key) => key !== event.node.key)
                    : [...prevState, event.node.key]
            );
        }

        return (
            <ModalContentContainer
                onSubmit={onModalSubmit}
                modifier={'types'}
                added={
                    showAdded ? (
                        <ModalContent
                            items={items}
                            onChange={onChange}
                            onOrder={onChangeOrder}
                        />
                    ) : null
                }
            >
                <Tree
                    expandedKeys={expanded}
                    onExpand={onExpand}
                    onSelect={onSelect}
                    checkStrictly={checkStrictly}
                    checkable
                    onCheck={onCheck}
                    checkedKeys={
                        checkStrictly
                            ? map(items, (item) => item.id)
                            : {
                                  checked: items,
                                  halfChecked: halfCheckedKeys,
                              }
                    }
                    treeData={treeData}
                />
            </ModalContentContainer>
        );
    }
);

export const ModalContentContainer = ({
    children,
    onSubmit,
    header,
    modifier,
    tabsClassname,
    added,
}) => {
    const isDesktop = useDesktop();
    const [activeTab, setActiveTab] = useState(1);
    const items = [
        {
            key: '1',
            label: `Все`,
            children: (
                <Space
                    direction={'vertical'}
                    style={{
                        width: '100%',
                        overflow: 'auto',
                    }}
                >
                    {children}
                </Space>
            ),
        },
        ...(added
            ? [
                  {
                      key: '2',
                      label: `Добавленные`,
                      children: (
                          <Space
                              direction={'vertical'}
                              style={{
                                  width: '100%',
                                  overflow: 'auto',
                              }}
                          >
                              {added}
                          </Space>
                      ),
                  },
              ]
            : []),
    ];
    return (
        <>
            <Space
                size={'middle'}
                direction={'vertical'}
                style={{ width: '100%', height: '100%' }}
                className={`lending-section-modal__space_${modifier}`}
            >
                {header}
                {isDesktop ? (
                    <Tabs
                        className={tabsClassname}
                        defaultActiveKey="1"
                        items={items}
                    />
                ) : (
                    <div>
                        <div>
                            <div className={s.row}>
                                <div
                                    className={
                                        activeTab === 1
                                            ? s.active_tab
                                            : s.common_tab
                                    }
                                    onClick={() => setActiveTab(1)}
                                >
                                    Все
                                </div>
                                {added && (
                                    <div
                                        className={`${s.second_tab} ${
                                            activeTab === 2
                                                ? s.active_tab
                                                : s.common_tab
                                        }`}
                                        onClick={() => setActiveTab(2)}
                                    >
                                        Добавленные
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={s.content}>
                            {activeTab === 1 ? children : added}
                        </div>
                    </div>
                )}
                <Button
                    style={{ width: '100%' }}
                    type={'primary'}
                    onClick={onSubmit}
                >
                    Сохранить
                </Button>
            </Space>
        </>
    );
};

export const LendingSectionModal = ({
    visible,
    children,
    onCancel,
    title,
    uniqueClass,
}) => {
    return (
        <CustomModal
            title={title}
            open={visible}
            onCancel={onCancel}
            className={'lending-section-modal'}
            uniqueClass={uniqueClass}
        >
            {children}
        </CustomModal>
    );
};
